/**
 * Step 4: Property (Optional)
 * Captures property ownership and sale plans.
 * Supports multiple properties: primary residence, buy-to-let, holiday let.
 */

import { useState, useEffect } from 'react';
import { useForm, useFieldArray, type UseFormRegister, type UseFormWatch, type UseFormSetValue, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step4FormData, Step4Schema, PropertyItem, type AllStepFormData } from '../../types/wizard';
import { useCurrencyInput } from '../../hooks/useCurrencyInput';
import { formatCurrency } from '../../utils/formatters';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP4_TOUR } from '../TourGuide/tourSteps';
import './Step4Property.css';

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  PRIMARY_RESIDENCE: 'Primary Residence',
  BUY_TO_LET: 'Buy-to-Let',
  HOLIDAY_LET: 'Holiday Let',
};

/**
 * Calculate Stamp Duty Land Tax (SDLT) on a residential property purchase.
 * Post-April 2025 main-residence bands, kept static.
 */
function calculateStampDuty(purchasePrice: number): number {
  if (purchasePrice <= 0) return 0;
  const bands = [
    { threshold: 125_000, rate: 0 },
    { threshold: 250_000, rate: 0.02 },
    { threshold: 925_000, rate: 0.05 },
    { threshold: 1_500_000, rate: 0.1 },
    { threshold: Infinity, rate: 0.12 },
  ];
  let tax = 0;
  let prev = 0;
  for (const band of bands) {
    if (purchasePrice <= prev) break;
    const taxable = Math.min(purchasePrice, band.threshold) - prev;
    tax += taxable * band.rate;
    prev = band.threshold;
  }
  return Math.round(tax * 100) / 100;
}

/**
 * Mortgage balance remaining after `yearsElapsed`, split into an interest-only portion (flat
 * until the mortgage end date, then paid off) and a repayment portion (amortises to £0 by the
 * end date). Mirrors the server's calculateMortgageBalance in propertyHandler.ts — kept as a
 * continuous-years approximation here (vs. the server's discrete projection-year steps) since
 * this only drives a wizard preview estimate, not the actual projection. If mortgageEndDate is
 * unset, the balance is assumed to stay flat (same convention as the engine).
 */
function estimateMortgageBalance(
  totalMortgage: number,
  interestOnlyAmount: number,
  interestRatePct: number,
  yearsElapsed: number,
  yearsToMortgageEnd: number | null,
): number {
  if (totalMortgage <= 0) return 0;
  if (yearsToMortgageEnd == null) return totalMortgage;
  if (yearsElapsed >= yearsToMortgageEnd) return 0;

  const interestOnlyPortion = Math.min(Math.max(interestOnlyAmount, 0), totalMortgage);
  const repaymentPortion = totalMortgage - interestOnlyPortion;

  let repaymentBalance = repaymentPortion;
  if (repaymentPortion > 0 && yearsToMortgageEnd > 0) {
    const rate = Math.max(interestRatePct, 0) / 100;
    if (rate <= 0) {
      repaymentBalance = repaymentPortion * Math.max(0, 1 - yearsElapsed / yearsToMortgageEnd);
    } else {
      const monthlyRate = rate / 12;
      const termMonths = yearsToMortgageEnd * 12;
      const monthsElapsed = yearsElapsed * 12;
      const powT = Math.pow(1 + monthlyRate, termMonths);
      const powM = Math.pow(1 + monthlyRate, monthsElapsed);
      repaymentBalance = Math.max(0, repaymentPortion * (powT - powM) / (powT - 1));
    }
  }

  return interestOnlyPortion + repaymentBalance;
}

/**
 * Translate a plan's legacy mortgageType/annualMortgageInterest fields (if present, and if the
 * new fields aren't already) into interestOnlyMortgageAmount/mortgageInterestRatePct. The old
 * annualMortgageInterest was a £ amount (for the BTL Section 24 credit), not a rate, so we back
 * out an implied rate from it rather than losing the data outright.
 */
function migrateLegacyMortgageFields<T extends {
  currentMortgageAmount?: number;
  interestOnlyMortgageAmount?: number;
  mortgageInterestRatePct?: number;
  mortgageType?: 'REPAYMENT' | 'INTEREST_ONLY';
  annualMortgageInterest?: number;
}>(p: T): T {
  if (p.interestOnlyMortgageAmount !== undefined || p.mortgageInterestRatePct !== undefined) {
    return p;
  }
  const balance = p.currentMortgageAmount ?? 0;
  // Capped at 20% (matches PropertyItemSchema's mortgageInterestRatePct bound) — a stale or
  // inconsistent legacy annualMortgageInterest £ figure against a since-changed balance can
  // otherwise imply an absurd rate that breaks the amortisation formula downstream.
  const impliedRatePct = balance > 0 && p.annualMortgageInterest
    ? Math.min(Math.round((p.annualMortgageInterest / balance) * 100 * 100) / 100, 20)
    : 0;
  return {
    ...p,
    interestOnlyMortgageAmount: p.mortgageType === 'INTEREST_ONLY' ? balance : 0,
    mortgageInterestRatePct: impliedRatePct,
  };
}

function createEmptyProperty(existingProperties: PropertyItem[]): PropertyItem {
  const hasPrimary = existingProperties.some(p => p.propertyType === 'PRIMARY_RESIDENCE');
  return {
    propertyId: crypto.randomUUID(),
    label: hasPrimary ? 'Investment Property' : 'Primary Residence',
    propertyType: hasPrimary ? 'BUY_TO_LET' : 'PRIMARY_RESIDENCE',
    owner: 'joint',
    currentMarketValue: 0,
    currentMortgageAmount: 0,
    currentEquity: 0,
    planningSale: false,
    plannedSaleDate: '',
    estateAgentFeePct: 1.5,
    newPropertyPurchasePrice: 0,
    proceedsStrategy: 'INVEST',
    monthlyMortgagePayment: 0,
    mortgageEndDate: '',
    purchasePrice: 0,
    acquisitionCosts: 0,
    improvementCosts: 0,
    interestOnlyMortgageAmount: 0,
    mortgageInterestRatePct: 0,
    legalFees: 0,
    rental: null,
  };
}

/** Migrate legacy single-property data to properties array format */
function migrateToProperties(initialData: Partial<AllStepFormData>): PropertyItem[] | undefined {
  if (initialData?.properties?.length) {
    return initialData.properties.map(migrateLegacyMortgageFields);
  }
  if (initialData?.property && initialData.includeProperty) {
    const p = initialData.property;
    return [{
      propertyId: 'property-0',
      label: 'Primary Residence',
      propertyType: 'PRIMARY_RESIDENCE' as const,
      owner: 'joint' as const,
      currentMarketValue: p.currentMarketValue ?? 0,
      currentMortgageAmount: p.currentMortgageAmount ?? 0,
      currentEquity: (p.currentMarketValue ?? 0) - (p.currentMortgageAmount ?? 0),
      planningSale: p.planningSale ?? false,
      plannedSaleDate: p.plannedSaleDate ?? '',
      estateAgentFeePct: p.estateAgentFeePct ?? 1.5,
      newPropertyPurchasePrice: p.newPropertyPurchasePrice ?? 0,
      proceedsStrategy: (p.proceedsStrategy ?? 'INVEST') as 'INVEST' | 'CASH',
      monthlyMortgagePayment: p.monthlyMortgagePayment ?? 0,
      mortgageEndDate: p.mortgageEndDate ?? '',
      purchasePrice: 0,
      acquisitionCosts: 0,
      improvementCosts: 0,
      interestOnlyMortgageAmount: 0,
      mortgageInterestRatePct: 0,
      legalFees: 0,
      rental: null,
    }];
  }
  return undefined;
}

interface Step4Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step4FormData) => void;
  onSave: (data: Step4FormData) => void;
  onBack?: () => void;
  onGoToStep?: (step: number) => void;
  lastSaved?: string;
  planType?: 'SINGLE' | 'JOINT';
  personNames?: [string, string];
}

function Step4Property({ initialData, onNext, onSave, onBack, lastSaved, planType, personNames }: Readonly<Step4Props>) {
  const migratedProperties = migrateToProperties(initialData ?? {});

  // Derive person names from initialData if not passed as prop
  const resolvedPersonNames: [string, string] = personNames ?? [
    initialData?.persons?.[0]?.fullName?.trim() || 'Person 1',
    initialData?.persons?.[1]?.fullName?.trim() || 'Person 2',
  ];

  // Derive planType from initialData if not passed as prop (default SINGLE hides Owner dropdown safely)
  const resolvedPlanType = planType ?? initialData?.planType ?? 'SINGLE';

  // Check if a care-triggered sale is configured in Step 7 (informational only — does not block Step 4)
  const hasCareTriggeredSale = (initialData?.careScenarios || []).some(
    (cs: { sellPrimaryResidenceOnCareStart?: boolean; careType?: string }) =>
      cs.sellPrimaryResidenceOnCareStart && (cs.careType === 'RESIDENTIAL' || cs.careType === 'NURSING')
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<Step4FormData>({
    resolver: zodResolver(Step4Schema),
    defaultValues: {
      includeProperty: initialData?.includeProperty || false,
      properties: migratedProperties || [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'properties',
  });

  const includeProperty = watch('includeProperty');
  const properties = watch('properties') || [];

  const handleBlur = () => {
    const data = watch();
    onSave(data);
  };

  const handleAddProperty = (currentProperties?: PropertyItem[]) => {
    append(createEmptyProperty(currentProperties ?? properties));
    requestAnimationFrame(() => {
      const cards = document.querySelectorAll('.property-card');
      cards[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleIncludePropertyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && fields.length === 0) {
      handleAddProperty([]);
    }
  };

  const handleRemoveProperty = (index: number) => {
    remove(index);
    setTimeout(handleBlur, 0);
  };

  const onSubmit = (data: Step4FormData) => {
    onNext(data);
  };

  // Derive array-level error message (react-hook-form v7 stores superRefine errors at .root)
  const propertiesRootError = errors.properties?.root?.message as string | undefined;

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Property</h2>
          <TourButton tour={WIZARD_STEP4_TOUR} variant="text" />
        </div>
        <p>{includeProperty && properties.length > 0
          ? `You have ${properties.length} propert${properties.length === 1 ? 'y' : 'ies'} in your plan`
          : 'Include property in your retirement plan (optional)'}</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        <div className="form-group">
          <label className="property-checkbox">
            <input
              type="checkbox"
              {...register('includeProperty', {
                onChange: handleIncludePropertyChange,
              })}
            />
            <span>I/We own property to include in this plan</span>
          </label>
        </div>

        {includeProperty && (
          <div className="property-fields">
            {fields.map((field, index) => (
              <PropertyDetailForm
                key={field.id}
                index={index}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                onBlur={handleBlur}
                onRemove={() => handleRemoveProperty(index)}
                canRemove={fields.length > 1}
                planType={resolvedPlanType}
                personNames={resolvedPersonNames}
                hasCareTriggeredSale={hasCareTriggeredSale && watch(`properties.${index}.propertyType`) === 'PRIMARY_RESIDENCE'}
                inflationRate={(initialData?.economicAssumptions?.inflationAnnualPct ?? 2.2) / 100}
                planAsOfDate={initialData?.planAsOfDate ?? ''}
              />
            ))}

            <button
              type="button"
              className="btn btn-secondary add-property-btn"
              onClick={() => handleAddProperty()}
            >
              + Add Property
            </button>

            {propertiesRootError && (
              <span className="error-message">{propertiesRootError}</span>
            )}

            {properties.some((p: PropertyItem) => p.propertyType === 'BUY_TO_LET' || p.propertyType === 'HOLIDAY_LET') && (
              <div className="property-assumptions-disclosure">
                <strong>Property tax assumptions</strong>
                <ul>
                  <li>Rental profit is taxed at your marginal income tax rate (UK rates used for all taxpayers, including Scottish residents whose rates may differ slightly).</li>
                  <li>Rental income is not subject to National Insurance.</li>
                  <li>If your expenses exceed rental income in a year, the loss is not carried forward to offset future profits.</li>
                  <li>The £1,000 property income allowance is not applied — we assume you are claiming actual expenses.</li>
                  <li>CGT on investment property sales uses residential rates (currently 18% basic / 24% higher).</li>
                  <li>Joint ownership assumes a 50/50 income split (HMRC default). Form 17 declarations for unequal splits are not modelled.</li>
                </ul>
                <p>This tool is for educational purposes only and does not constitute tax advice. Consult a qualified tax adviser for your specific circumstances.</p>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Income
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ============================================
// Property Detail Form (per property)
// ============================================

interface PropertyDetailFormProps {
  index: number;
  register: UseFormRegister<Step4FormData>;
  watch: UseFormWatch<Step4FormData>;
  setValue: UseFormSetValue<Step4FormData>;
  errors: FieldErrors<Step4FormData>;
  onBlur: () => void;
  onRemove: () => void;
  canRemove: boolean;
  planType?: 'SINGLE' | 'JOINT';
  personNames?: [string, string];
  hasCareTriggeredSale?: boolean;
  inflationRate: number;
  planAsOfDate: string;
}

function PropertyDetailForm({
  index,
  register,
  watch,
  setValue,
  errors,
  onBlur,
  onRemove,
  canRemove,
  planType,
  personNames,
  hasCareTriggeredSale,
  inflationRate,
  planAsOfDate,
}: Readonly<PropertyDetailFormProps>) {
  const prefix = `properties.${index}` as const;
  const propErrors = errors?.properties?.[index];

  const label = watch(`${prefix}.label`);
  const propertyType = watch(`${prefix}.propertyType`);
  const marketValue = watch(`${prefix}.currentMarketValue`);
  const mortgageAmount = watch(`${prefix}.currentMortgageAmount`);
  const planningSale = watch(`${prefix}.planningSale`);
  const newPropertyPrice = watch(`${prefix}.newPropertyPurchasePrice`);
  const estateAgentFeePct = watch(`${prefix}.estateAgentFeePct`);
  const monthlyMortgagePayment = watch(`${prefix}.monthlyMortgagePayment`);
  const mortgageEndDate = watch(`${prefix}.mortgageEndDate`);
  const plannedSaleDate = watch(`${prefix}.plannedSaleDate`);
  const interestOnlyMortgageAmount = watch(`${prefix}.interestOnlyMortgageAmount`);
  const mortgageInterestRatePct = watch(`${prefix}.mortgageInterestRatePct`);

  const isPrimary = propertyType === 'PRIMARY_RESIDENCE';
  const isInvestment = propertyType === 'BUY_TO_LET' || propertyType === 'HOLIDAY_LET';

  // Track replacement property intent — initialised from saved data, reset on type change
  const [buyingReplacement, setBuyingReplacement] = useState((newPropertyPrice || 0) > 0);

  // Derived values — inflate market value to planned sale date
  const yearsToSale = (() => {
    if (!plannedSaleDate || !planAsOfDate) return 0;
    const saleMs = new Date(plannedSaleDate).getTime();
    const startMs = new Date(planAsOfDate).getTime();
    if (Number.isNaN(saleMs) || Number.isNaN(startMs)) return 0;
    return Math.max(0, (saleMs - startMs) / (365.25 * 24 * 60 * 60 * 1000));
  })();
  const yearsToMortgageEnd = (() => {
    if (!mortgageEndDate || !planAsOfDate) return null;
    const endMs = new Date(mortgageEndDate).getTime();
    const startMs = new Date(planAsOfDate).getTime();
    if (Number.isNaN(endMs) || Number.isNaN(startMs)) return null;
    return Math.max(0, (endMs - startMs) / (365.25 * 24 * 60 * 60 * 1000));
  })();
  const inflationMultiplier = Math.pow(1 + inflationRate, yearsToSale);
  const inflatedMarketValue = (marketValue || 0) * inflationMultiplier;
  const inflatedMortgage = estimateMortgageBalance(
    mortgageAmount || 0,
    interestOnlyMortgageAmount || 0,
    mortgageInterestRatePct || 0,
    yearsToSale,
    yearsToMortgageEnd,
  );
  const computedEquity = inflatedMarketValue - inflatedMortgage;
  const agentFees = inflatedMarketValue * ((estateAgentFeePct || 0) / 100);
  const inflatedNewPropertyPrice = (newPropertyPrice || 0) * inflationMultiplier;
  const stampDuty = isPrimary ? calculateStampDuty(inflatedNewPropertyPrice) : 0;
  const netPropertyPot = computedEquity - agentFees - (isPrimary ? inflatedNewPropertyPrice : 0) - stampDuty;
  const isInflated = yearsToSale > 0.5;
  // Derived alongside yearsToSale so the "inflated to <year>" caption cannot be
  // reached without a date: yearsToSale is 0 when plannedSaleDate is unset, so
  // isInflated is false — a correlation the compiler cannot see on its own.
  const saleYear = plannedSaleDate ? new Date(plannedSaleDate).getFullYear() : null;

  // Keep hidden equity input in sync with today's value (not inflated — engine handles inflation)
  const todayEquity = (marketValue || 0) - (mortgageAmount || 0);
  useEffect(() => {
    setValue(`${prefix}.currentEquity`, todayEquity, { shouldValidate: true });
  }, [todayEquity, prefix, setValue]);

  const marketValueInput = useCurrencyInput({
    value: marketValue,
    registerProps: register(`${prefix}.currentMarketValue`, { valueAsNumber: true }),
  });
  const mortgageInput = useCurrencyInput({
    value: mortgageAmount,
    registerProps: register(`${prefix}.currentMortgageAmount`, { valueAsNumber: true }),
  });
  const newPropertyInput = useCurrencyInput({
    value: newPropertyPrice,
    registerProps: register(`${prefix}.newPropertyPurchasePrice`, { valueAsNumber: true }),
  });
  const mortgagePaymentInput = useCurrencyInput({
    value: monthlyMortgagePayment,
    registerProps: register(`${prefix}.monthlyMortgagePayment`, { valueAsNumber: true }),
  });

  const handleMarketValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replaceAll(',', '');
    const newMarketValue = Number.parseFloat(rawValue) || 0;
    setValue(`${prefix}.currentEquity`, newMarketValue - (mortgageAmount || 0), { shouldValidate: true });
  };
  const handleMortgageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replaceAll(',', '');
    const newMortgage = Number.parseFloat(rawValue) || 0;
    setValue(`${prefix}.currentEquity`, (marketValue || 0) - newMortgage, { shouldValidate: true });
  };

  // When type changes, toggle rental fields and reset replacement property state
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as PropertyItem['propertyType'];
    setValue(`${prefix}.propertyType`, newType);
    if (newType === 'PRIMARY_RESIDENCE') {
      setValue(`${prefix}.rental`, null);
    } else {
      if (!watch(`${prefix}.rental`)) {
        setValue(`${prefix}.rental`, { annualGrossIncome: 0, annualAllowableExpenses: 0, occupancyRatePct: 90 });
      }
      // Reset replacement property when switching away from primary residence
      setBuyingReplacement(false);
      setValue(`${prefix}.newPropertyPurchasePrice`, 0);
    }
    onBlur();
  };

  return (
    <div className="property-card">
      <div className="property-card-header">
        <h3>{label || `Property ${index + 1}`}</h3>
        <span className={`property-type-badge property-type-badge--${(propertyType ?? 'PRIMARY_RESIDENCE').toLowerCase().replaceAll('_', '-')}`}>{(propertyType && PROPERTY_TYPE_LABELS[propertyType]) || propertyType}</span>
        {canRemove && (
          <button type="button" className="btn-remove" onClick={onRemove} title="Remove property">
            Remove
          </button>
        )}
      </div>

      {/* Property Basics */}
      <div className="property-basics">
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Property name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Family Home, BTL Manchester"
              {...register(`${prefix}.label`)}
              onBlur={onBlur}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Type</label>
            <select
              className="form-input"
              value={propertyType}
              onChange={handleTypeChange}
            >
              <option value="PRIMARY_RESIDENCE">Primary Residence</option>
              <option value="BUY_TO_LET">Buy-to-Let</option>
              <option value="HOLIDAY_LET">Holiday Let</option>
            </select>
          </div>
          {planType === 'JOINT' && (
            <div className="form-group">
              <label className="form-label">Owner</label>
              <select
                className="form-input"
                {...register(`${prefix}.owner`)}
                onBlur={onBlur}
              >
                <option value="joint">Joint</option>
                <option value="person0">{personNames?.[0] || 'Person 1'}</option>
                <option value="person1">{personNames?.[1] || 'Person 2'}</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Value Section */}
      <div className="form-group">
        <label className="form-label" htmlFor={`properties.${index}.currentMarketValue`}>Current Market Value (£) <span className="required">*</span></label>
        <input
          id={`properties.${index}.currentMarketValue`}
          type="text"
          className={`form-input ${propErrors?.currentMarketValue ? 'error' : ''}`}
          {...marketValueInput}
          onChange={(e) => { marketValueInput.onChange(e); handleMarketValueChange(e); }}
          onBlur={(e) => { marketValueInput.onBlur(e); onBlur(); }}
        />
        {propErrors?.currentMarketValue && <span className="error-message">{propErrors.currentMarketValue.message}</span>}
      </div>

      <div className="form-group">
        <span className="form-label">Your equity</span>
        <span className="equity-display" data-testid="equity-display">
          {formatCurrency((marketValue || 0) - (mortgageAmount || 0), true)}
        </span>
        <input type="hidden" {...register(`${prefix}.currentEquity`, { valueAsNumber: true })} />
        {propErrors?.currentEquity && <span className="error-message">{propErrors.currentEquity.message}</span>}
      </div>

      {/* Purchase History (investment properties) */}
      {isInvestment && (
        <div className="form-section">
          <h4>Purchase History</h4>
          <span className="help-text">When you sell an investment property, you may owe Capital Gains Tax on any profit. Enter your purchase details so we can estimate this.</span>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Original Purchase Price (£)</label>
              <input type="number" className="form-input" {...register(`${prefix}.purchasePrice`, { valueAsNumber: true })} onBlur={onBlur} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor={`${prefix}.acquisitionCosts`}>Purchase Costs (£)</label>
              <input id={`${prefix}.acquisitionCosts`} type="number" className={`form-input ${propErrors?.acquisitionCosts ? 'error' : ''}`} {...register(`${prefix}.acquisitionCosts`, { valueAsNumber: true })} onBlur={onBlur} />
              {propErrors?.acquisitionCosts && <span className="error-message">{propErrors.acquisitionCosts.message}</span>}
              <span className="help-text">Stamp duty (SDLT), your solicitor's conveyancing fees and a survey you commissioned. Not mortgage arrangement fees or the lender's valuation: those are costs of the loan, which HMRC does not allow.</span>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Improvement Costs (£)</label>
              <input type="number" className="form-input" {...register(`${prefix}.improvementCosts`, { valueAsNumber: true })} onBlur={onBlur} />
              <span className="help-text">Extensions, conversions, or other improvements that added value</span>
            </div>
            <div className="form-group">
              <label className="form-label">Legal Fees on Sale (£)</label>
              <input type="number" className="form-input" {...register(`${prefix}.legalFees`, { valueAsNumber: true })} onBlur={onBlur} />
            </div>
          </div>
        </div>
      )}

      {/* Mortgage Details */}
      <div className="form-section">
        <h4>Mortgage</h4>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Outstanding Mortgage (£)</label>
            <input
              type="text"
              className={`form-input ${propErrors?.currentMortgageAmount ? 'error' : ''}`}
              data-testid="outstanding-mortgage-input"
              {...mortgageInput}
              onChange={(e) => { mortgageInput.onChange(e); handleMortgageChange(e); }}
              onBlur={(e) => { mortgageInput.onBlur(e); onBlur(); const ma = (Number.parseFloat(e.target.value.replaceAll(',', '')) || 0); if (ma === 0) { setValue(`${prefix}.monthlyMortgagePayment`, 0); setValue(`${prefix}.interestOnlyMortgageAmount`, 0); setValue(`${prefix}.mortgageInterestRatePct`, 0); setValue(`${prefix}.mortgageEndDate`, ''); } }}
            />
            {propErrors?.currentMortgageAmount && <span className="error-message">{propErrors.currentMortgageAmount.message}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Monthly Payment (£)</label>
            <input
              type="text"
              className="form-input"
              {...mortgagePaymentInput}
              onBlur={(e) => { mortgagePaymentInput.onBlur(e); onBlur(); }}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input type="date" className="form-input" {...register(`${prefix}.mortgageEndDate`)} onBlur={onBlur} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Interest Rate (%)</label>
          <input type="number" step="0.01" className="form-input" {...register(`${prefix}.mortgageInterestRatePct`, { valueAsNumber: true })} onBlur={onBlur} />
          {isInvestment && <span className="help-text">Landlords receive a 20% tax credit on mortgage interest paid</span>}
          <span className="help-text">Applies to both portions below</span>
        </div>

        {(mortgageAmount || 0) > 0 && (
          <div className="mortgage-structure-split">
            <div className="mortgage-structure-panel">
              <label className="form-label">Interest-only portion (£)</label>
              <input
                type="number"
                className={`form-input ${propErrors?.interestOnlyMortgageAmount ? 'error' : ''}`}
                {...register(`${prefix}.interestOnlyMortgageAmount`, { valueAsNumber: true })}
                onBlur={onBlur}
              />
              {propErrors?.interestOnlyMortgageAmount && <span className="error-message">{propErrors.interestOnlyMortgageAmount.message}</span>}
              <span className="help-text">Balance never reduces — the full amount is owed at the end date</span>
            </div>
            <div className="mortgage-structure-panel">
              <label className="form-label">Repayment portion (£)</label>
              <input
                type="number"
                className="form-input"
                data-testid="repayment-portion-input"
                value={Math.max(0, (mortgageAmount || 0) - (interestOnlyMortgageAmount || 0))}
                onChange={(e) => {
                  const newRepayment = Math.max(0, Number.parseFloat(e.target.value) || 0);
                  setValue(`${prefix}.currentMortgageAmount`, (interestOnlyMortgageAmount || 0) + newRepayment, { shouldValidate: true });
                }}
                onBlur={onBlur}
              />
              <span className="help-text">Reduces to £0 by the end date — editing this adjusts the total mortgage above</span>
            </div>
          </div>
        )}
      </div>

      {/* Rental Income (BTL/Holiday Let) */}
      {isInvestment && (
        <div className="form-section">
          <h4>Rental Income</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Annual Gross Income (£)</label>
              <input type="number" className="form-input" {...register(`${prefix}.rental.annualGrossIncome`, { valueAsNumber: true })} onBlur={onBlur} />
            </div>
            <div className="form-group">
              <label className="form-label">Annual Allowable Expenses (£)</label>
              <input type="number" className="form-input" {...register(`${prefix}.rental.annualAllowableExpenses`, { valueAsNumber: true })} onBlur={onBlur} />
              <span className="help-text">Letting agent fees, insurance, repairs, maintenance, replacement furnishings, accountancy. Do not include mortgage interest.</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Occupancy Rate (%)</label>
            <input type="number" step="1" min="0" max="100" className="form-input" {...register(`${prefix}.rental.occupancyRatePct`, { valueAsNumber: true })} onBlur={onBlur} />
            <span className="help-text">Percentage of the year the property is let out (e.g. 90 means 10% void periods)</span>
          </div>
          <div className="property-tax-note">
            <strong>How we model rental income:</strong> Rental profit (income minus expenses) is taxed at your marginal income tax rate. Mortgage interest is not deducted from profit — instead, landlords receive a 20% tax credit (Section 24). Rental income grows with inflation; mortgage interest stays fixed at the amount you enter. Joint-owned properties split income 50/50 between partners.
          </div>
        </div>
      )}

      {/* Sale Planning */}
      <div className="form-group nested-section">
        <label className="property-checkbox">
          <input type="checkbox" {...register(`${prefix}.planningSale`, { onChange: () => { setTimeout(onBlur, 0); } })} />
          <span>Planning to sell this property?</span>
        </label>
      </div>
      {hasCareTriggeredSale && isPrimary && (
        <div className="form-group nested-section">
          <p className="help-text assumptions-note">
            Your Step 7 care scenario includes selling your home when care begins.
            {planningSale
              ? ' Any replacement property you buy here will be the one sold at that point.'
              : ' This property will be sold at that point.'}
          </p>
        </div>
      )}

      {planningSale && (
        <div className="sale-fields">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Planned Sale Date</label>
              <input type="date" className="form-input" {...register(`${prefix}.plannedSaleDate`)} onBlur={onBlur} />
            </div>
            <div className="form-group">
              <label className="form-label">Estate Agent Fee (%)</label>
              <input type="number" step="0.01" className="form-input" {...register(`${prefix}.estateAgentFeePct`, { valueAsNumber: true })} onBlur={onBlur} />
            </div>
          </div>

          {isPrimary && (
            <div className="form-section">
              <div className="form-group">
                <label className="property-checkbox">
                  <input
                    type="checkbox"
                    checked={buyingReplacement}
                    onChange={(e) => {
                      setBuyingReplacement(e.target.checked);
                      if (!e.target.checked) {
                        setValue(`${prefix}.newPropertyPurchasePrice`, 0, { shouldValidate: true });
                        onBlur();
                      }
                    }}
                  />
                  <span>Buying a replacement property?</span>
                </label>
              </div>
              {buyingReplacement && (
                <div className="form-group">
                  <label className="form-label">Replacement property price (£)</label>
                  <input
                    type="text"
                    className="form-input"
                    {...newPropertyInput}
                    onBlur={(e) => { newPropertyInput.onBlur(e); onBlur(); }}
                  />
                </div>
              )}
            </div>
          )}

          <fieldset className="form-group">
            <legend className="form-label">What should we do with the sale proceeds?</legend>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  value="INVEST"
                  {...register(`${prefix}.proceedsStrategy`, { onChange: () => { setTimeout(onBlur, 0); } })}
                />
                <div>
                  <span className="radio-label">Invest the proceeds</span>
                  <span className="help-text">Add to a general investment account (stocks and shares), growing at your chosen return rate. Gains on withdrawal are subject to Capital Gains Tax.</span>
                </div>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  value="CASH"
                  {...register(`${prefix}.proceedsStrategy`, { onChange: () => { setTimeout(onBlur, 0); } })}
                />
                <div>
                  <span className="radio-label">Keep as cash savings</span>
                  <span className="help-text">Hold in a savings account, earning a lower cash-like return. Interest is taxed as savings income (Personal Savings Allowance applies).</span>
                </div>
              </label>
            </div>
          </fieldset>

          {/* Property Pot Summary (primary residence only) */}
          {isPrimary && computedEquity > 0 && (
            <div className="property-pot-summary">
              <h4 className="property-pot-title">
                Estimated Net Property Pot
                {isInflated && saleYear !== null
                  ? ` (inflated to ${saleYear})`
                  : ' (today\u2019s values)'}
              </h4>
              <table className="property-pot-table">
                <tbody>
                  {isInflated && (
                    <tr>
                      <td>Estimated market value at sale</td>
                      <td className="property-pot-value">{formatCurrency(inflatedMarketValue, true)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>{isInflated ? 'Less outstanding mortgage (estimated at sale)' : 'Current equity'}</td>
                    <td className={`property-pot-value${isInflated ? ' negative' : ''}`}>
                      {isInflated ? <>&minus;{formatCurrency(inflatedMortgage, true)}</> : formatCurrency(computedEquity, true)}
                    </td>
                  </tr>
                  <tr>
                    <td>Less estate agent fees ({estateAgentFeePct || 0}%)</td>
                    <td className="property-pot-value negative">&minus;{formatCurrency(agentFees, true)}</td>
                  </tr>
                  {(newPropertyPrice || 0) > 0 && (
                    <>
                      <tr>
                        <td>{isInflated ? 'Less new property purchase (inflated)' : 'Less new property purchase'}</td>
                        <td className="property-pot-value negative">&minus;{formatCurrency(isInflated ? inflatedNewPropertyPrice : (newPropertyPrice || 0), true)}</td>
                      </tr>
                      <tr>
                        <td>Less estimated stamp duty (SDLT)</td>
                        <td className="property-pot-value negative">&minus;{formatCurrency(stampDuty, true)}</td>
                      </tr>
                    </>
                  )}
                  <tr className="property-pot-total">
                    <td>Net property pot</td>
                    <td className={`property-pot-value${netPropertyPot < 0 ? ' negative' : ''}`}>
                      {formatCurrency(Math.abs(netPropertyPot), true)}
                      {netPropertyPot < 0 && <span className="property-pot-warning"> (negative)</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
              {isInflated && (mortgageAmount || 0) > 0 && yearsToMortgageEnd == null && (
                <span className="help-text warning-text">
                  No mortgage end date set — showing today&rsquo;s balance unchanged. Add an end date above to estimate how much will actually be left by the sale date.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Step4Property;
